import * as React from 'react'
import ReactTooltip from 'react-tooltip'

import { TimelineProps } from './timelineProps'

import ActivityTooltipRenderer from '../components/tooltips/renderers/ActivityTooltipRenderer'
import EventTooltipRenderer from '../components/tooltips/renderers/EventTooltipRenderer'
import WeeklyRestTooltipRenderer from '../components/tooltips/renderers/WeeklyRestTooltipRenderer'
import PlaceItemToolTipRenderer from '../components/tooltips/renderers/PlaceItemTooltipRenderer'
import WeekRecompenseTooltipRenderer from '../components/tooltips/renderers/WeekecompenseTooltipRenderer'
import TmsDataItemTooltipRenderer from '../components/tooltips/renderers/TmsDataItemTooltipRenderer'
import ExistingDelegationItemTooltipRenderer from '../components/tooltips/renderers/ExistingDelegationItemTooltipRenderer'

import { createTimelineScene } from './scene/TimelineScene'
import { SceneProvider } from './context/SceneContext'
import { DataProvider, TimelineData } from './context/DataContext'
import { ConfigProvider, TimelineConfig } from './context/ConfigContext'
import { LanesProvider } from './context/LanesContext'
import { buildRestSpans, createRestLaneResolver } from './layers/lanes'
import { LayerFlags } from './layers/types'
import { LAYERS } from './layers/registry'
import { getVisibleActivities } from './utils/activityVisibility'

const Timeline = (props: TimelineProps) => {
  const [hideTooltips, setHideTooltips] = React.useState(false)

  const scene = React.useMemo(
    () =>
      createTimelineScene({
        startTime: props.startTime,
        days: props.days ?? 1,
        width: props.width,
        height: props.height,
        weekly: props.weekly,
      }),
    [
      props.startTime?.valueOf?.(),
      props.days,
      props.width,
      props.height,
      props.weekly,
    ],
  )

  const latest = React.useRef(props)
  latest.current = props

  const stable = React.useMemo(
    () => ({
      translate: (text: string) => latest.current.translate(text),
      onSelected: (activity: any) => latest.current.onSelected?.(activity),
      onEdit: (activity: any, operation: any) =>
        latest.current.onEdit?.(activity, operation),
      onAdd: (activity: any) => latest.current.onAdd?.(activity),
      onRestClick: (activity: any) => latest.current.onRestClick?.(activity),
      onDaySelected: (date: any) => latest.current.onDaySelected?.(date),
      onViolationSelected: () => latest.current.onViolationSelected?.(),
      onPeriodSelectionsChanged: (selections: any) =>
        latest.current.onPeriodSelectionsChanged?.(selections),
      editEvent: (event: any, shouldEdit: boolean) =>
        latest.current.editEvent?.(event, shouldEdit),
      addEvent: (event: any) => latest.current.addEvent?.(event),
      removeEvents: (events: any) => latest.current.removeEvents?.(events),
      hideDelegationMarkerPopups: (event: any) =>
        latest.current.hideDelegationMarkerPopups?.(event),
      markNewEvent: (event: any) => latest.current.markNewEvent?.(event),
      onActivityContextMenu: (activity: any, x: number, y: number) =>
        latest.current.onActivityContextMenu?.(activity, x, y),
    }),
    [],
  )

  const onContextMenuChanged = React.useCallback(
    (value: boolean, activity: any) => {
      setHideTooltips(value)
      latest.current.onContextMenuChanged?.(value, activity)
    },
    [],
  )

  const data = React.useMemo<TimelineData>(
    () => ({
      activities: getVisibleActivities(props.activities || []),
      vehicleSourceActivities: props.vehicleSourceActivities || [],
      breaks: props.breaks || [],
      places: props.places || [],
      cardInserts: props.cardInserts || [],
      dayActivities: props.dayActivities || [],
      nightWorkActivities: props.nightWorkActivities || [],
      violations: props.violations || [],
      dailyRests: props.dailyRests || [],
      dailyPartialRegularRests: props.dailyPartialRegularRests || [],
      weeklyRests: props.weeklyRests || [],
      weeklyReducedRests: props.weeklyReducedRests || [],
      partialBreaks: props.partialBreaks || [],
      weekRecompenses: props.weekRecompenses || [],
      outOfScopes: props.outOfScopes || [],
      tmsData: props.tmsData || [],
      minimalPaymentCountries: props.minimalPaymentCountries || [],
      changeAreas: props.changeAreas || [],
      existingDelegations: props.existingDelegations || [],
      periodSelections: props.periodSelections || [],
      delegationMarkers: props.delegationMarkers || [],
      allTripEvents: props.allTripEvents || [],
      allPreviousTripEvents: props.allPreviousTripEvents || [],
      allEvents: props.allEvents || [],
      countries: props.countries || [],
      selectedActivity: props.selectedActivity,
      editedActivity: props.editedActivity,
      isAddingNew: !!props.isAddingNew,
      currentDate: props.extraCurrentDate,
    }),
    [
      props.activities,
      props.vehicleSourceActivities,
      props.breaks,
      props.places,
      props.cardInserts,
      props.dayActivities,
      props.nightWorkActivities,
      props.violations,
      props.dailyRests,
      props.dailyPartialRegularRests,
      props.weeklyRests,
      props.weeklyReducedRests,
      props.partialBreaks,
      props.weekRecompenses,
      props.outOfScopes,
      props.tmsData,
      props.minimalPaymentCountries,
      props.changeAreas,
      props.existingDelegations,
      props.periodSelections,
      props.delegationMarkers,
      props.allTripEvents,
      props.allPreviousTripEvents,
      props.allEvents,
      props.countries,
      props.selectedActivity,
      props.editedActivity,
      props.isAddingNew,
      props.extraCurrentDate,
    ],
  )

  const restLanes = React.useMemo(
    () => createRestLaneResolver(buildRestSpans(data)),
    [data],
  )


  React.useEffect(() => {
    const id = requestAnimationFrame(() => ReactTooltip.rebuild())
    return () => cancelAnimationFrame(id)
  }, [data, scene, hideTooltips])

  const config = React.useMemo<TimelineConfig>(
    () => ({
      flattenChart: !!props.flattenChart,
      simpleMode: !!props.simpleMode,
      editMode: !!props.editMode,
      canEdit: !!props.canEdit,
      canGenerateAttestation: !!props.canGenerateAttestation,
      disableTooltips: !!props.disableTooltips,
      readOnly: !!props.readOnly,
      printMode: !!props.printMode,
      showRestLabels: !!props.showRestLabels,
      isRestsOnBaseModeOn: !!props.isRestsOnBaseModeOn,
      minimalPaymentSuppressedPerSpan: !!props.minimalPaymentSuppressedPerSpan,
      stackOverlappingMarkers: !!props.stackOverlappingMarkers,
      dimOtherTripMarkers: !!props.dimOtherTripMarkers,
      markInactiveVehicles: !!props.markInactiveVehicles,
      translate: stable.translate,
      onSelected: stable.onSelected,
      onEdit: stable.onEdit,
      onAdd: stable.onAdd,
      onRestClick: stable.onRestClick,
      onDaySelected: stable.onDaySelected,
      onContextMenuChanged,
      onActivityContextMenu: props.onActivityContextMenu
        ? stable.onActivityContextMenu
        : undefined,
      onViolationSelected: stable.onViolationSelected,
      onPeriodSelectionsChanged: stable.onPeriodSelectionsChanged,
      editEvent: stable.editEvent,
      addEvent: stable.addEvent,
      removeEvents: stable.removeEvents,
      hideDelegationMarkerPopups: stable.hideDelegationMarkerPopups,
      markNewEvent: stable.markNewEvent,
    }),
    [
      props.flattenChart,
      props.simpleMode,
      props.editMode,
      props.canEdit,
      props.canGenerateAttestation,
      props.disableTooltips,
      props.readOnly,
      props.printMode,
      props.showRestLabels,
      props.isRestsOnBaseModeOn,
      props.minimalPaymentSuppressedPerSpan,
      props.stackOverlappingMarkers,
      props.dimOtherTripMarkers,
      props.markInactiveVehicles,
      stable,
      onContextMenuChanged,
      !!props.onActivityContextMenu,
    ],
  )

  const flags: LayerFlags = {
    simpleMode: !!props.simpleMode,
    editMode: !!props.editMode,
    showRests: props.showRests !== false,
    showViolations: !!props.showViolations,
    showDelegations: !!props.showDelegations,
    showRestParts: !!props.showRestParts,
    showDayActivities: props.showDayActivities !== false,
    showTrips: !!props.showTrips,
  }

  const visibleLayers = LAYERS.filter((layer) => layer.isVisible(flags)).sort(
    (a, b) => a.zIndex - b.zIndex,
  )

  const showTooltips =
    !props.editMode &&
    props.editedActivity == null &&
    !props.disableTooltips &&
    !hideTooltips

  return (
    <SceneProvider value={scene}>
      <ConfigProvider value={config}>
        <DataProvider value={data}>
          <LanesProvider value={restLanes}>
            <div
            className="tw-bg-white"
              style={{
                height: scene.height,
           
                width: props.width,
                position: 'relative',
                borderColor: props.editMode ? '#b2b2b2' : 'transparent',
                borderStyle: 'dashed',
                borderWidth: 2,
              }}
            >
              <div style={{ opacity: props.editMode ? 0.7 : 1 }}>
                <div style={{ opacity: props.opacity ?? 1 }}>
                  {visibleLayers.map((layer) => (
                    <layer.Component key={layer.id} zIndex={layer.zIndex} />
                  ))}
                </div>

                {showTooltips && (
                  <div key={`tooltips-${hideTooltips}-${props.showViolations}`}>
                    <ActivityTooltipRenderer
                      translate={props.translate}
                      startTime={props.startTime}
                    />
                    <EventTooltipRenderer
                      translate={props.translate}
                      startTime={props.startTime}
                    />
                    <WeeklyRestTooltipRenderer
                      translate={props.translate}
                      startTime={props.startTime}
                    />
                    <PlaceItemToolTipRenderer
                      translate={props.translate}
                      startTime={props.startTime}
                    />
                    <WeekRecompenseTooltipRenderer
                      translate={props.translate}
                      startTime={props.startTime}
                    />
                  </div>
                )}

                {!hideTooltips && (
                  <div key={`extra-tooltips-${hideTooltips}`}>
                    <TmsDataItemTooltipRenderer
                      translate={props.translate}
                      startTime={props.startTime}
                    />
                    {props.showTrips && (
                      <ExistingDelegationItemTooltipRenderer
                        translate={props.translate}
                        startTime={props.startTime}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </LanesProvider>
        </DataProvider>
      </ConfigProvider>
    </SceneProvider>
  )
}

export default Timeline
